import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Save, Loader2, Upload, Globe, Mail, Palette, Image, CheckCircle2, X, Trash2,
} from 'lucide-react';

import { BACKEND_URL } from '@/config';

const BrandingPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [settings, setSettings] = useState({
    platform_name: 'Common Impact E-learning',
    platform_logo_url: '',
    platform_favicon_url: '',
    support_email: '',
    primary_brand_color: '#20acf6',
    secondary_brand_color: '#02d1d4',
    social_links: { twitter: '', facebook: '', linkedin: '', instagram: '' },
    email_footer_text: '',
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      const d = res.data;
      setSettings({
        platform_name: d.platform_name || 'Common Impact E-learning',
        platform_logo_url: d.platform_logo_url || '',
        platform_favicon_url: d.platform_favicon_url || '',
        support_email: d.support_email || '',
        primary_brand_color: d.primary_brand_color || '#20acf6',
        secondary_brand_color: d.secondary_brand_color || '#02d1d4',
        social_links: d.social_links || { twitter: '', facebook: '', linkedin: '', instagram: '' },
        email_footer_text: d.email_footer_text || '',
      });
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Branding settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/x-icon', 'image/svg+xml', 'image/vnd.microsoft.icon'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|ico|svg)$/i)) {
      toast.error('Please upload a JPG, PNG, WebP, ICO, or SVG file.');
      return;
    }

    const setter = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setter(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/cms/settings/upload-logo?type=${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.url || res.data.logo_url;
      const field = type === 'logo' ? 'platform_logo_url' : 'platform_favicon_url';
      setSettings(prev => ({ ...prev, [field]: url }));

      // Auto-save the URL to settings
      await api.put('/settings', { [field]: url });

      toast.success(
        type === 'logo'
          ? 'Logo uploaded! It will now appear in the site header.'
          : 'Favicon uploaded! It will now appear as the browser tab icon.'
      );
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed. Please try a different file.';
      toast.error(detail);
    } finally {
      setter(false);
      // Reset the input so the same file can be re-uploaded
      e.target.value = '';
    }
  };

  const clearImage = async (type) => {
    const field = type === 'logo' ? 'platform_logo_url' : 'platform_favicon_url';
    setSettings(prev => ({ ...prev, [field]: '' }));
    try {
      await api.put('/settings', { [field]: '' });
      toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} removed`);
    } catch { toast.error('Failed to remove'); }
  };

  const updateSocial = (key, value) => {
    setSettings(p => ({ ...p, social_links: { ...p.social_links, [key]: value } }));
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin']}>
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6 max-w-3xl" data-testid="branding-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold">Branding</h1>
            <p className="text-[#64748b] mt-1 text-sm">Customise your platform appearance</p>
          </div>
          <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Changes
          </Button>
        </div>

        {/* Identity */}
        <Card className="border-[#cbd5e1]/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Palette className="h-5 w-5 text-[#20acf6]" /> Brand Identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Site Name</Label>
              <Input value={settings.platform_name} onChange={e => setSettings(p => ({ ...p, platform_name: e.target.value }))} data-testid="site-name-input" />
            </div>
            <div>
              <Label>Support Email</Label>
              <Input type="email" value={settings.support_email} onChange={e => setSettings(p => ({ ...p, support_email: e.target.value }))} data-testid="support-email-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Primary Colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.primary_brand_color} onChange={e => setSettings(p => ({ ...p, primary_brand_color: e.target.value }))} className="w-10 h-10 rounded border cursor-pointer" data-testid="primary-color-input" />
                  <Input value={settings.primary_brand_color} onChange={e => setSettings(p => ({ ...p, primary_brand_color: e.target.value }))} className="flex-1" />
                </div>
              </div>
              <div>
                <Label>Secondary Colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.secondary_brand_color} onChange={e => setSettings(p => ({ ...p, secondary_brand_color: e.target.value }))} className="w-10 h-10 rounded border cursor-pointer" data-testid="secondary-color-input" />
                  <Input value={settings.secondary_brand_color} onChange={e => setSettings(p => ({ ...p, secondary_brand_color: e.target.value }))} className="flex-1" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logo & Favicon */}
        <Card className="border-[#cbd5e1]/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Image className="h-5 w-5 text-[#20acf6]" /> Logo & Favicon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-8">
              {/* Logo Upload */}
              <div>
                <Label className="mb-3 block text-sm font-medium">Site Logo</Label>
                <div className="border-2 border-dashed border-[#cbd5e1] rounded-xl p-6 text-center hover:border-[#20acf6] transition-colors" data-testid="logo-upload-zone">
                  {settings.platform_logo_url ? (
                    <div className="space-y-3">
                      <div className="bg-[#f8fafc] rounded-lg p-4 inline-block">
                        <img
                          src={resolveUrl(settings.platform_logo_url)}
                          alt="Logo preview"
                          className="h-16 w-auto max-w-[200px] object-contain"
                          data-testid="logo-preview"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="flex items-center justify-center gap-1 text-xs text-[#02d1d4]">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Logo uploaded
                      </div>
                      <div className="flex gap-2 justify-center">
                        <label className="cursor-pointer">
                          <Button variant="outline" size="sm" asChild><span><Upload className="h-3.5 w-3.5 mr-1.5" /> Replace</span></Button>
                          <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.svg" onChange={e => handleUpload(e, 'logo')} data-testid="logo-upload-input" />
                        </label>
                        <Button variant="outline" size="sm" onClick={() => clearImage('logo')} className="text-[#ff6b6b] hover:text-[#ff6b6b]" data-testid="logo-remove-btn">
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <div className="space-y-2">
                        <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center mx-auto">
                          <Upload className="h-6 w-6 text-[#94a3b8]" />
                        </div>
                        <p className="text-sm font-medium text-[#1e293b]">
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </p>
                        <p className="text-xs text-[#94a3b8]">JPG, PNG, or WebP. Appears in the site header.</p>
                      </div>
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.svg" onChange={e => handleUpload(e, 'logo')} disabled={uploadingLogo} data-testid="logo-upload-input" />
                    </label>
                  )}
                  {uploadingLogo && <div className="mt-3"><Loader2 className="h-5 w-5 animate-spin text-[#20acf6] mx-auto" /></div>}
                </div>
              </div>

              {/* Favicon Upload */}
              <div>
                <Label className="mb-3 block text-sm font-medium">Browser Favicon</Label>
                <div className="border-2 border-dashed border-[#cbd5e1] rounded-xl p-6 text-center hover:border-[#20acf6] transition-colors" data-testid="favicon-upload-zone">
                  {settings.platform_favicon_url ? (
                    <div className="space-y-3">
                      <div className="bg-[#f8fafc] rounded-lg p-4 inline-block">
                        <img
                          src={resolveUrl(settings.platform_favicon_url)}
                          alt="Favicon preview"
                          className="h-10 w-10 object-contain"
                          data-testid="favicon-preview"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="flex items-center justify-center gap-1 text-xs text-[#02d1d4]">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Favicon uploaded
                      </div>
                      <div className="flex gap-2 justify-center">
                        <label className="cursor-pointer">
                          <Button variant="outline" size="sm" asChild><span><Upload className="h-3.5 w-3.5 mr-1.5" /> Replace</span></Button>
                          <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.ico,.svg" onChange={e => handleUpload(e, 'favicon')} data-testid="favicon-upload-input" />
                        </label>
                        <Button variant="outline" size="sm" onClick={() => clearImage('favicon')} className="text-[#ff6b6b] hover:text-[#ff6b6b]" data-testid="favicon-remove-btn">
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <div className="space-y-2">
                        <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center mx-auto">
                          <Upload className="h-6 w-6 text-[#94a3b8]" />
                        </div>
                        <p className="text-sm font-medium text-[#1e293b]">
                          {uploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                        </p>
                        <p className="text-xs text-[#94a3b8]">JPG, PNG, ICO, or SVG. Appears as the browser tab icon.</p>
                      </div>
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.ico,.svg" onChange={e => handleUpload(e, 'favicon')} disabled={uploadingFavicon} data-testid="favicon-upload-input" />
                    </label>
                  )}
                  {uploadingFavicon && <div className="mt-3"><Loader2 className="h-5 w-5 animate-spin text-[#20acf6] mx-auto" /></div>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card className="border-[#cbd5e1]/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-[#20acf6]" /> Social Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {['twitter', 'facebook', 'linkedin', 'instagram'].map(key => (
              <div key={key}>
                <Label className="capitalize">{key}</Label>
                <Input placeholder={`https://${key}.com/yourpage`} value={settings.social_links?.[key] || ''} onChange={e => updateSocial(key, e.target.value)} data-testid={`social-${key}-input`} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Email Footer */}
        <Card className="border-[#cbd5e1]/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-[#20acf6]" /> Email Footer</CardTitle>
          </CardHeader>
          <CardContent>
            <Label>Footer Text (appears in all platform emails)</Label>
            <Textarea value={settings.email_footer_text} onChange={e => setSettings(p => ({ ...p, email_footer_text: e.target.value }))} placeholder="e.g. Common Impact Ltd, 123 Care Street, London..." rows={3} data-testid="email-footer-input" />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BrandingPage;
